/**
 * Every `data-action` in the application, in one place.
 *
 * Views render; they do not wire. Keeping the handlers together means the set of
 * things a person can do is readable in one file, and it means a handler that
 * mutates state and re-fetches has exactly one shape: act, report, reload.
 *
 * IDEMPOTENCY KEYS
 *   Every mutating call to `apps/api` requires one. They are minted per intent
 *   with `crypto.randomUUID()` rather than derived from a counter, because a
 *   replay is only safe when the same key is reused by the same attempt and not
 *   by a second, different attempt.
 */

import { fail, type Failure, ok, type Result } from '../api/client.js';
import * as api from '../api/queries.js';
import type { App } from '../app.js';
import { clearStoredSession, storeSession, storeTheme } from '../app.js';
import { contentBinding } from '../lib/review-model.js';
import {
  DEFAULT_SKILLS_QUERY,
  hrefFor,
  parseSkillsQuery,
  safeReturnPath,
  skillsQueryToSearch,
} from '../lib/router.js';
import { clearUnsaved, saveUnsaved } from '../lib/unsaved.js';
import { presentFailure } from '../ui/components.js';
import { DRAFT_DOCUMENT_PATH, type EditorData } from './editor.js';
import type { ReviewData } from './review.js';
import { type AuthorizeData, type ConsentDecision, submitConsent } from './consent.js';
import { publishAvailability } from './publication.js';
import { registerLifecycleActions } from './lifecycle.js';

/** Where focus goes back to when the one dialog closes. */
let returnFocusId: string | null = null;

/**
 * Record the control that opened the dialog, BY ID.
 *
 * The id, not the element: `App.paint` replaces the whole subtree, so the node
 * that was clicked is gone by the time the dialog closes and only a name for it
 * survives. That is also why every opener carries an id at all — a button
 * without one records `null` and the return silently does nothing, which is how
 * "Use this skill" and "New skill" both measured as failing to return focus
 * (real Chromium, 2026-10-01: `id === ''` on both, and `document.activeElement`
 * is `document.body` after Escape).
 */
function noteDialogReturnFocus(element: HTMLElement): void {
  returnFocusId = element.id === '' ? null : element.id;
}

export function consumeDialogReturnFocus(): string | null {
  const id = returnFocusId;
  returnFocusId = null;
  return id;
}

function key(): string {
  return crypto.randomUUID();
}

function report(app: App, failure: Failure): void {
  const presented = presentFailure(failure);
  app.notify('bad', `${presented.title}. ${presented.body}`);
}

function value(element: HTMLElement): string {
  if (element instanceof HTMLInputElement || element instanceof HTMLTextAreaElement)
    return element.value;
  if (element instanceof HTMLSelectElement) return element.value;
  return '';
}

/**
 * Keep the reason typed in the editor, keyed by the person and the draft.
 *
 * This is the write side of V1 section 9's "preserve unsaved text through
 * recoverable failures". It runs on input rather than on a timer, because the
 * failures it has to outlive — an expired session that navigates to `/login`,
 * a reload, a refused save — all end this tab's memory sooner than any timer
 * would fire. See `lib/unsaved.ts` for which reading of section 9 this meets
 * and which it does not.
 */
function keepEditorUnsaved(app: App): void {
  const session = app.state.session;
  const screen = currentScreenData<EditorData>(app);
  const draft = screen === null ? null : screen.active;
  if (session === null || draft === null) return;
  saveUnsaved(session.userId, draft.id, { note: app.state.note, source: app.state.source });
}

/**
 * Drop the kept reason, from a success branch only.
 *
 * Stale text restored over a draft that has moved on is worse than lost text:
 * the person would be editing a reason recorded against a version that no
 * longer exists, and nothing on screen would say so.
 */
function dropEditorUnsaved(app: App): void {
  const session = app.state.session;
  const screen = currentScreenData<EditorData>(app);
  const draft = screen === null ? null : screen.active;
  if (session === null || draft === null) return;
  clearUnsaved(session.userId, draft.id);
}

/**
 * Prove that a save which reported success actually wrote the document.
 *
 * `saveDraft` answering with a version is the server's word for it. This is the
 * screen's own check, and it exists because the success branch is the exact
 * moment unsaved work is destroyed: it drops the kept copy. A save that
 * persists nothing and returns a version anyway would therefore take the only
 * copy of the text with it, which is a worse outcome than refusing to save.
 *
 * Three things are read back, and none of them is the save's own answer:
 *
 *   1. the screen reloads, so the draft row comes from a fresh read;
 *   2. the reloaded draft is the same draft, at the version the save returned;
 *   3. the bytes for `DRAFT_DOCUMENT_PATH` come from `GET /v1/drafts/:id/files`,
 *      a different route from the `PUT` that wrote them, and one that checks
 *      what it serves against the digest the row recorded.
 *
 * Failing any of them reports and keeps everything. The honest reading of "the
 * save said it worked and the draft does not show it" is that we do not know
 * that it worked — not that it did.
 */
async function confirmDraftWrite(
  app: App,
  draftId: string,
  version: number,
  content: string,
): Promise<Result<true>> {
  await app.reload();

  const screen = app.state.screen;
  if (screen.kind === 'failed') return fail(screen.failure);
  if (screen.kind !== 'ready') {
    return fail({
      kind: 'server',
      detail:
        'The save reported success and the editor did not come back to check it, so this cannot ' +
        'be called a saved draft. Your text has been kept.',
      requestId: null,
    });
  }

  const reloaded = (screen.data as EditorData).active;
  if (reloaded === null || reloaded.id !== draftId || reloaded.version !== version) {
    return fail({
      kind: 'conflict',
      reason: 'draft_version_conflict',
      currentVersion: reloaded?.version ?? null,
      detail:
        `The save reported version ${version}, and reloading this draft shows ` +
        `${reloaded === null ? 'no draft at all' : `version ${reloaded.version}`}. ` +
        'Your text has been kept and nothing was cleared.',
    });
  }

  const stored = await api.readDraftFile(app.client, draftId, DRAFT_DOCUMENT_PATH);
  if (!stored.ok) return stored;
  if (stored.value !== content) {
    return fail({
      kind: 'invalid',
      reason: 'draft_content_mismatch',
      detail:
        `The draft saved, but the ${DRAFT_DOCUMENT_PATH} stored for it is not the text that was ` +
        'sent. Your text has been kept — copy it somewhere safe before trying again.',
    });
  }

  return ok(true);
}

/**
 * ============================================================================
 * ENDING A SESSION. Three parts, and the order between them is the fix.
 * ============================================================================
 *
 * WHY ANY OF THIS IS A SERVER CALL
 *   The credential this browser hands to `apps/api` is not in the page. It is
 *   `BEARER_COOKIE`, an `HttpOnly` cookie that `apps/web/src/server.ts`'s
 *   `proxy()` reads and presents as `Authorization: Bearer` (server.ts:536-539)
 *   because a browser following a redirect cannot set that header itself. A
 *   `sessionStorage.clear()` therefore ends the page and not the session: the
 *   jar still holds a token, every later request is still made as somebody, and
 *   `GET /api/v1/memberships` answers 200 with the roster. That is measured, and
 *   it is the state this exists to end.
 *
 * WHAT IT DOES NOT REVOKE — V1:113's line, kept intact
 *   "Browser logout ends the local session; 'Disconnect all clients'
 *   separately revokes MCP grants." This is the first clause. Nothing here
 *   touches `app.client_grants`, an MCP session, or a connected client, and no
 *   reader should take a green here as evidence about any of them.
 *
 *   Nor does it revoke the token. It cannot: the token is signed by `apps/api`
 *   with a secret this process is deliberately not given, and the API is what
 *   would have to honour a revocation. So the operation here is credential
 *   DESTRUCTION — the browser is left holding nothing — and that is the whole
 *   of it, stated so it is not read as more.
 */
const SIGN_OUT_PATH = '/auth/signout';
const SIGN_OUT_TIMEOUT_MS = 10_000;

/**
 * `credentials: 'same-origin'`, which is the opposite of what `api/client.ts`
 * uses for the API and is not an inconsistency.
 *
 *   The API requests are `omit` because every one of them carries its own
 *   `Authorization` header from `sessionStorage` and must not also present a
 *   cookie. This one has no header to carry — the cookie IS the credential, and
 *   the point of the request is to have the server destroy it. It also goes to
 *   THIS origin, not to `apps/api`: the credential belongs to the web origin,
 *   so ending the session has to be asked of the origin that issued it.
 *
 * Returns whether the server was reached and agreed. `false` is reported to the
 * person rather than hidden, because "I signed out" and "I signed out and the
 * server knows" are different facts on a shared machine.
 */
async function endBrowserSession(): Promise<boolean> {
  try {
    const response = await fetch(SIGN_OUT_PATH, {
      method: 'POST',
      credentials: 'same-origin',
      signal: AbortSignal.timeout(SIGN_OUT_TIMEOUT_MS),
    });
    return response.ok;
  } catch {
    return false;
  }
}

/**
 * Telling this browser's OTHER tabs, which a cookie cannot reach.
 *
 *   `sessionStorage` is per-tab by specification, so the token a second tab read
 *   out of the sign-in cookie is a copy nothing here can reach. A `BroadcastChannel`
 *   is same-origin and cross-tab, which is precisely the shape of the problem:
 *   one browser, many tabs, one credential to retire.
 *
 *   What it is NOT: a revocation. It reaches other tabs of THIS browser and
 *   nothing else. A token copied onto another machine is unaffected by anything
 *   in this file, and saying otherwise would be the kind of claim that reads as
 *   a security control and is not one.
 */
const SIGN_OUT_CHANNEL = 'skills-manager:sign-out';
const SIGN_OUT_MESSAGE = 'signed-out';

/**
 * `null` rather than a throw, for two reasons: this module is imported by
 * tests and a build step that may have no DOM at all, and a browser that lacks
 * the API should still be able to sign out — losing cross-tab propagation is a
 * degradation, not a failure, and the cookie has already been destroyed by then.
 */
function signOutChannel(): BroadcastChannel | null {
  try {
    return typeof BroadcastChannel === 'undefined' ? null : new BroadcastChannel(SIGN_OUT_CHANNEL);
  } catch {
    return null;
  }
}

/**
 * The subscription each tab holds, so that a tab can give it back.
 *
 *   A `BroadcastChannel` is the one resource in this file that the whole
 *   process can see, and its listener closes over the `App` it was opened for.
 *   An app that is never told to stop listening is therefore never collectable,
 *   and every sign-out another tab posts runs that app's handler — against a
 *   page that is no longer on screen, calling into storage it no longer has.
 *   In a browser the page's own teardown closes the channel for it; nothing
 *   else can, which is why the subscription has to be releasable from here.
 *
 *   Keyed by the app, so `registerActions` called twice on one app opens one
 *   subscription rather than two handlers firing per sign-out. Weak, so holding
 *   this map does not itself keep an app alive — the channel does that, and
 *   only until `stopWatchingForSignOut` closes it.
 */
const signOutWatchers = new WeakMap<App, BroadcastChannel>();

/** One message, from the tab that pressed the control. It never hears itself. */
function announceSignOut(): void {
  const channel = signOutChannel();
  if (channel === null) return;
  channel.postMessage(SIGN_OUT_MESSAGE);
  channel.close();
}

function watchForSignOut(app: App): void {
  if (signOutWatchers.has(app)) return;
  const channel = signOutChannel();
  if (channel === null) return;
  signOutWatchers.set(app, channel);
  // Held open for the life of the tab — it is a listener, not a one-shot — and
  // unref'd so a test process that registered handlers and then stopped is not
  // kept alive by a channel nobody is waiting on.
  // Present in Bun and in Node, absent from the DOM lib this package types
  // against — hence the check rather than a cast: a browser that has no
  // `unref` simply keeps the process alive, which is correct there.
  (channel as BroadcastChannel & { unref?: () => void }).unref?.();
  channel.addEventListener('message', (event: MessageEvent) => {
    if (event.data !== SIGN_OUT_MESSAGE) return;
    // Exactly the local teardown, and deliberately NOT a different one: a
    // second tab ends up in the state an expiry ends up in, which is the state
    // this application already knows how to render.
    //
    // It navigates to `/login` even though nobody asked that tab to go
    // anywhere. `adoptSession(null)` alone would leave a signed-out tab showing
    // a roster it had already fetched — the page claiming a session that no
    // longer exists, which is the shape of the defect rather than its repair.
    // `navigate()` still asks before discarding unsaved text (app.ts:294-297),
    // and `lib/unsaved.ts` has already put that text in this tab's own storage,
    // so declining costs the person nothing they typed.
    clearStoredSession();
    app.adoptSession(null);
    app.navigate('/login', { replace: true });
  });
}

/**
 * Ends the cross-tab watch for `app`, and is the counterpart of the
 * `watchForSignOut` call inside `registerActions`.
 *
 *   Safe to call for an app that never watched — the browser closes the channel
 *   when the page goes away, so a tab never has to — and safe to call twice.
 *   What it must not do is leave the channel open: a listener that outlives the
 *   tab it was opened for is a callback into a document that is gone, which is
 *   exactly what makes a test process non-deterministic.
 */
export function stopWatchingForSignOut(app: App): void {
  const channel = signOutWatchers.get(app);
  if (channel === undefined) return;
  signOutWatchers.delete(app);
  channel.close();
}

export function registerActions(app: App): void {
  /* ---- shell -------------------------------------------------------- */

  app.action('theme:toggle', () => {
    storeTheme(app.state.theme === 'dark' ? 'light' : 'dark');
  });

  // The organization selector. Navigating rather than mutating anything: the
  // slug in the URL is re-checked against the verified token by `App.load`
  // (app.ts:309-312), so a slug this session is not bound to ends at not-found
  // rather than at a screen for an organization the token does not name. The
  // control therefore cannot become a way to act in the wrong tenant, which is
  // the whole reason it is a navigation and not a stored preference.
  app.action('field:org.select', (element) => {
    const slug = (element as HTMLSelectElement).value;
    const route = app.state.route;
    if (slug === '' || !('org' in route)) return;
    app.navigate(hrefFor({ ...route, org: slug }));
  });

  app.action('sign-out', async () => {
    // THE SERVER GOES FIRST, and that ordering is the fix rather than a detail.
    // `endBrowserSession()` expires `BEARER_COOKIE`, which is what `proxy()`
    // presents as `Authorization: Bearer`; the two calls after it drop the
    // tab's own copy. Clearing the tab first and calling afterwards would leave
    // a window in which the screen says signed out and the credential is not —
    // which is exactly the state the audit measured as 200 with the roster.
    const ended = await endBrowserSession();
    clearStoredSession();
    app.adoptSession(null);
    announceSignOut();
    app.navigate('/login', { replace: true });

    // Reported, not swallowed, and NOT a reason to stay on the page: someone at
    // a shared machine must always be able to leave, even with the network
    // down. What they are told is which of the two things failed.
    if (!ended) {
      app.notify(
        'bad',
        'Signed out on this tab, but the server could not be reached to end the ' +
          'session this browser hands to the server on every request. Close every ' +
          'tab of this site before leaving this machine.',
      );
    }
  });

  // A second tab holds its own copy of the token, in its own `sessionStorage`
  // — which is per-tab, so nothing this one drops reaches it. The cookie is
  // gone for the whole browser the moment the response above lands, so a
  // request arriving there without an `Authorization` header of its own is
  // already refused; what is left is a tab that still LOOKS signed in and can
  // still read what it already had in memory. This is the same teardown, told
  // to the other tabs rather than done twice by hand.
  watchForSignOut(app);

  app.action('notice:clear', () => app.clearNotice());
  app.action('skill:noop', () => {});

  /* ---- library ------------------------------------------------------ */

  app.action('library:reload', () => app.reload());
  app.action('library:goto-admin', () => app.navigate(`/o/${app.orgSlug}/admin`));
  app.action('library:clear-filters', () =>
    app.setSearch(
      skillsQueryToSearch({
        ...DEFAULT_SKILLS_QUERY,
        scope: parseSkillsQuery(app.state.search).scope,
      }),
    ),
  );
  app.action('library:new-skill', (element) => {
    noteDialogReturnFocus(element);
    app.patch({ dialog: 'new-skill' });
  });
  for (const scope of ['published', 'drafts'] as const) {
    app.action(`library:scope:${scope}`, () => {
      const query = parseSkillsQuery(app.state.search);
      app.setSearch(skillsQueryToSearch({ ...query, scope }));
    });
  }
  // The ONE field on this screen a person types a word into, and therefore the
  // one whose change is a burst rather than a decision. `owner` and `status`
  // below are choices: a person picks one and waits for the answer, so they ask
  // immediately. Measured 2026-10-01, real Chromium, 7 characters of "payment"
  // cost 58 requests through the immediate path — every keystroke revalidated
  // the shell as well as the listing.
  app.action('field:library.q', (element) => {
    const query = parseSkillsQuery(app.state.search);
    // `state.search` already carries the text typed so far: `setSearchDebounced`
    // moves the state on every keystroke and defers only the request. So a
    // change to another filter mid-burst still reads the newest term, and a
    // person who picks a status while typing does not lose what they typed.
    app.setSearchDebounced(skillsQueryToSearch({ ...query, q: value(element) }));
  });
  app.action('field:library.owner', (element) => {
    const query = parseSkillsQuery(app.state.search);
    app.setSearch(skillsQueryToSearch({ ...query, owner: value(element) }));
  });
  app.action('field:library.status', (element) => {
    const query = parseSkillsQuery(app.state.search);
    app.setSearch(skillsQueryToSearch({ ...query, status: value(element) }));
  });

  app.action('gate:new-skill-submit', async () => {
    const title = document.querySelector<HTMLInputElement>('#new-skill-title');
    const slug = document.querySelector<HTMLInputElement>('#new-skill-slug');
    const session = app.state.session;
    if (title === null || slug === null || session === null) return;
    const created = await api.createSkill(
      app.client,
      {
        slug: slug.value.trim(),
        title: title.value.trim(),
      },
      key(),
    );
    if (!created.ok) {
      report(app, created.failure);
      return;
    }
    app.patch({ dialog: null });
    app.notify(
      'ok',
      `Created ${created.value.title}. It is unpublished and private to you until you grant it.`,
    );
    app.navigate(`/o/${app.orgSlug}/skills/${created.value.id}/edit`);
  });
  app.action('dialog:close', () => app.patch({ dialog: null }));

  /* ---- toolkits ----------------------------------------------------- */

  app.action('toolkits:reload', () => app.reload());
  app.action('toolkits:new', (element) => {
    noteDialogReturnFocus(element);
    app.patch({ dialog: 'new-toolkit' });
  });
  app.action('gate:new-toolkit-submit', async () => {
    const title = document.querySelector<HTMLInputElement>('#new-toolkit-title');
    const slug = document.querySelector<HTMLInputElement>('#new-toolkit-slug');
    const session = app.state.session;
    if (title === null || slug === null || session === null) return;
    const created = await api.createToolkit(
      app.client,
      {
        slug: slug.value.trim(),
        title: title.value.trim(),
      },
      key(),
    );
    if (!created.ok) {
      report(app, created.failure);
      return;
    }
    app.patch({ dialog: null });
    app.notify('ok', `Created the toolkit ${created.value.title}.`);
    await app.reload();
  });

  /* ---- skill detail ------------------------------------------------- */

  app.action('skill:reload', () => app.reload());
  for (const tab of ['overview', 'files', 'history', 'improvements'] as const) {
    app.action(`skill:tab:${tab}`, () => {
      const params = new URLSearchParams(app.state.search);
      params.set('tab', tab);
      app.setSearch(`?${params.toString()}`);
    });
  }
  app.action('skill:edit', () => {
    const skill = currentSkillId(app);
    if (skill !== null) app.navigate(`/o/${app.orgSlug}/skills/${skill}/edit`);
  });
  app.action('skill:goto-improvements', () => app.navigate(`/o/${app.orgSlug}/improvements`));
  app.action('skill:export', async (element) => {
    // Every opener of the one dialog records where focus goes back to, and this
    // handler was the one that did not: Escape on the export dialog left
    // `document.activeElement` as `document.body` (measured 2026-10-01, real
    // Chromium), because `noteDialogReturnFocus` was never called and
    // `App.syncDialog` had nothing to consume — V1 section 9 asks for modal
    // focus return, and a dialog reached from three different buttons has to
    // return to whichever of them was used.
    noteDialogReturnFocus(element);
    const skill = currentSkillId(app);
    if (skill === null) return;
    const body = await api.downloadBundle(app.client, skill);
    if (!body.ok) {
      report(app, body.failure);
      return;
    }
    app.patch({ dialog: 'export', exportBody: body.value });
  });
  app.action('skill:download', async () => {
    const skill = currentSkillId(app);
    if (skill === null) return;
    // The whole release as a standard folder, not `SKILL.md` on its own.
    //
    // `downloadBundle` returns the one file the bundle route serves, and saving
    // that was called "Download release" — which is a name for something the
    // bytes were not. A release's `references/`, `assets/` and `scripts/` are
    // part of what was published and part of what the digest covers, so a
    // control that saved one of them gave away a file that cannot be re-imported
    // into anything: `packages/folder/src/import.ts` reads a standard folder, and
    // a bare `SKILL.md` is a folder with every supporting file missing.
    const archive = await api.downloadExport(app.client, skill);
    if (!archive.ok) {
      report(app, archive.failure);
      return;
    }
    const blob = new Blob([archive.value], { type: 'application/zip' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = `${currentSlug(app) ?? 'skill'}.zip`;
    anchor.click();
    URL.revokeObjectURL(url);
    // The last thing a person reads before the bytes are theirs, and the only
    // place this screen can say what the thing in their downloads folder is.
    // Both halves are load-bearing: what they hold, and the fact that nothing
    // here reaches them afterwards. `docs/V1-IMPLEMENTATION.md:127` (DIST-02)
    // requires the UI to make no promise about a file already on someone's
    // disk, so the second half is stated outright rather than left to be
    // inferred from silence.
    app.notify(
      'ok',
      'The release was served and recorded as a retrieval. You now have the whole folder and its manifest. It does not update itself, and nothing here reaches back into a file you have saved.',
    );
  });

  /* ---- editor ------------------------------------------------------- */

  app.action('editor:reload', () => app.reload());
  app.action('field:editor.note', (element) => {
    app.patch({ note: value(element), dirty: true });
    keepEditorUnsaved(app);
  });
  app.action('field:editor.source', (element) => {
    // `paint()` replaces the whole document on every state change, so the
    // textarea's value has to live in state or the keystroke that triggered
    // this render is the one that gets thrown away.
    app.patch({ source: value(element), dirty: true });
    keepEditorUnsaved(app);
  });
  app.action('editor:create', async () => {
    const data = currentScreenData<EditorData>(app);
    const skill = data?.skill ?? null;
    if (data === null || skill === null) return;
    // Two-phase by design: the create names no organization, no author and no
    // body at all, because the route fills the first two from the verified
    // token and refuses the third. What comes back is the row, and its `id` is
    // what the storage key is built from — so a refusal here wrote nothing and
    // there is nothing to clean up beyond saying so.
    const created = await api.createDraft(app.client, skill.id, key());
    if (!created.ok) {
      // The reason is carried on the screen data so the panel renders it beside
      // the control that failed, with the same control offered again. A notice
      // alone would be a line of text that scrolls away with no way back to
      // the button; and success is never announced on this branch.
      app.setScreen({
        kind: 'ready',
        data: { ...data, createDraftFailure: created.failure },
      });
      return;
    }
    // Reload rather than patch: the new draft has files, a version and a
    // storage key that only a fresh read knows, and the screen's whole job is
    // to show what is stored.
    await app.reload();
    const screen = app.state.screen;
    if (screen.kind !== 'ready' || (screen.data as EditorData).active?.id !== created.value.id) {
      // The create reported success and the screen cannot show the draft. That
      // is not a success, and saying so would be the lie this project keeps
      // refusing; the screen's own failed branch renders the reason.
      report(app, {
        kind: 'server',
        detail:
          `The draft was created (${created.value.id.slice(0, 8)}) but reloading this screen ` +
          'does not show it, so nothing is claimed about its contents.',
        requestId: null,
      });
      return;
    }
    app.notify(
      'ok',
      `Draft ${created.value.id.slice(0, 8)} created. It is private to you until you submit it for review.`,
    );
  });
  app.action('editor:save', async () => {
    const data = currentScreenData<EditorData>(app);
    const session = app.state.session;
    if (data === null || session === null || data.active === null) return;
    // The one file this screen edits, sent as content. The route derives the
    // digest, the byte count and the storage key from what arrives; a client
    // that sent a digest would be vouching for a hash of bytes it did not
    // write, so none is computed here.
    const content = app.state.source;
    const saved = await api.saveDraft(
      app.client,
      data.active.skill_id,
      data.active.id,
      data.active.version,
      app.state.note,
      key(),
      [{ path: DRAFT_DOCUMENT_PATH, content }],
    );
    if (!saved.ok) {
      // The text stays exactly where it is. A refusal never clears a field.
      report(app, saved.failure);
      return;
    }
    // Reported success is not proof of a write, so the draft is re-read before
    // the kept copy goes. Everything after this point — the drop, the clean
    // `dirty` flag and the notice that says a version was saved — is
    // conditional on that check, and a failure here leaves the text alone.
    const landed = await confirmDraftWrite(app, data.active.id, saved.value.version, content);
    if (!landed.ok) {
      report(app, landed.failure);
      return;
    }
    dropEditorUnsaved(app);
    app.patch({ dirty: false });
    app.notify('ok', `Draft saved at version ${saved.value.version}.`);
  });
  app.action('editor:submit', async () => {
    const data = currentScreenData<EditorData>(app);
    if (data === null || data.active === null) return;
    const submitted = await api.setDraftState(
      app.client,
      data.active.id,
      'in_review',
      app.state.note,
    );
    if (!submitted.ok) {
      report(app, submitted.failure);
      return;
    }
    // Submitting does NOT write the document and does NOT record the reason:
    // the route carries no body and refuses every field one could hold. So the
    // kept copy stays and `dirty` stays true — dropping them here would destroy
    // unsaved text on the strength of a transition that stored none of it, which
    // is the one thing section 9 forbids. Save first if the text is meant to
    // survive a reload.
    app.notify(
      'ok',
      'Submitted for review. A different person must decide it before anything is ' +
        'published. Unsaved text is still unsaved.',
    );
    await app.reload();
  });

  /* ---- review ------------------------------------------------------- */

  app.action('review:reload', () => app.reload());
  app.action('field:review.note', (element) => app.patch({ note: value(element) }));
  app.action('field:review.check.instructions', (element) =>
    app.patch({ checks: { ...app.state.checks, instructions: checked(element) } }),
  );
  app.action('field:review.check.references', (element) =>
    app.patch({ checks: { ...app.state.checks, references: checked(element) } }),
  );
  app.action('field:review.check.checkpoints', (element) =>
    app.patch({ checks: { ...app.state.checks, checkpoints: checked(element) } }),
  );

  app.action('review:approve', async () => {
    const data = currentScreenData<ReviewData>(app);
    const session = app.state.session;
    if (data === null || session === null) return;
    const binding = contentBinding(data.draftFiles);
    // The hash the reviewer is approving is the one computed from the files they
    // were shown, and `app.approve_draft` recomputes it from the locked draft and
    // refuses if the two disagree (PUB-01). A screen that computed it wrongly is
    // refused rather than producing an approval bound to nothing.
    const review = await api.recordReview(
      app.client,
      {
        draftId: data.draft.id,
        decision: 'approved',
        approvedContentHash: binding.hash,
        checklist: { ...app.state.checks, note: app.state.note },
      },
      crypto.randomUUID(),
    );
    if (!review.ok) {
      report(app, review.failure);
      return;
    }
    // NO second state write. `app.approve_draft` moves the draft to `approved`
    // inside the same transaction that records the review, so calling
    // `setDraftState` afterwards was not a confirmation — it was a request the
    // API refuses on principle ("only a draft in review can be submitted"), and
    // reporting its refusal would have told a reviewer their approval failed
    // immediately after it succeeded.
    app.notify('ok', `Approved and bound to content hash ${binding.hash}. Nothing is published until a separate, explicit Publish step.`);
    await app.reload();
  });

  app.action('review:request-changes', async () => {
    const data = currentScreenData<ReviewData>(app);
    if (data === null) return;
    // Refused here rather than at the RPC, and only because a blank reason has
    // exactly one possible outcome: the server's 422. Sending it would replace
    // "tell the author what to change" with "reason required", which is the same
    // instruction and a worse experience.
    if (app.state.note.trim() === '') {
      app.notify('bad', 'Say what should change before sending this back. The author has to act on it.');
      return;
    }
    const review = await api.recordReview(
      app.client,
      {
        draftId: data.draft.id,
        decision: 'changes_requested',
        approvedContentHash: null,
        checklist: { ...app.state.checks, note: app.state.note },
        reason: app.state.note,
      },
      crypto.randomUUID(),
    );
    if (!review.ok) {
      report(app, review.failure);
      return;
    }
    // As above: `app.request_changes` moves the draft back to `draft` itself.
    app.notify('ok', 'Change requested. The draft is back with its author.');
    await app.reload();
  });

  /* ---- publication ---------------------------------------------------- */
  //
  // PUBLISH IS A SEPARATE ACT FROM APPROVING, and V1 §6 step 6 is why it is a
  // separate button rather than a checkbox on the decision: a person who
  // approves content and a person who makes it current are two acts, and the
  // approval is bound to a content hash while the publication is not.
  //
  // A RETRY IS A NEW REQUEST WITH A NEW KEY, never a replay of the old one.
  // `app.publish_draft` looks `p_idempotency_key` up BEFORE the draft-state check
  // (`0033:1312-1333`), so re-sending the original key returns the ORIGINAL job
  // — including its `last_error` — and a person trying to leave a failure behind
  // would be shown it again. A fresh UUID is the honest retry, and it is what
  // creates the NEW job row `0033:1335-1339` describes.
  app.action('publication:publish', async () => {
    await requestPublication(app);
  });

  app.action('publication:retry', async () => {
    await requestPublication(app);
  });
  /* ---- lifecycle ------------------------------------------------------ */
  //
  // Deprecate and retire. Registered from their own file so this lane's four
  // handlers can be read without reading the editor, the review queue and the
  // consent screen to reach them; `app.action` is the same registry either way.
  registerLifecycleActions(app);
  /* ---- improvements -------------------------------------------------- */

  app.action('improvements:reload', () => app.reload());
  app.action('improvements:show-open', () => app.setSearch('?status=open'));
  app.action('improvements:propose', () =>
    app.notify(
      'bad',
      'Proposing a revision needs the client write path this deployment does not expose to the browser. An agent client with the proposal capability is the supported route.',
    ),
  );
  app.action('improvements:request', async (element) => {
    const proposalId = (element.dataset.action ?? '').split(':')[2] ?? '';
    const note =
      document.querySelector<HTMLTextAreaElement>(`#proposal-note-${proposalId}`)?.value.trim() ??
      '';
    await resolveProposal(app, proposalId, 'accepted', note);
  });
  app.action('improvements:accept', async (element) => {
    const proposalId = (element.dataset.action ?? '').split(':')[2] ?? '';
    const note =
      document.querySelector<HTMLTextAreaElement>(`#proposal-note-${proposalId}`)?.value.trim() ??
      '';
    await resolveProposal(app, proposalId, 'accepted', note);
  });
  app.action('improvements:dismiss', async (element) => {
    const proposalId = (element.dataset.action ?? '').split(':')[2] ?? '';
    const note =
      document.querySelector<HTMLTextAreaElement>(`#proposal-note-${proposalId}`)?.value.trim() ??
      '';
    await resolveProposal(app, proposalId, 'dismissed', note);
  });
  app.action('field:improvements.status', (element) => {
    app.setSearch(`?status=${encodeURIComponent(value(element))}`);
  });

  /* ---- connect ------------------------------------------------------- */

  app.action('connect:reload', () => app.reload());
  app.action('connect:goto-admin', () => app.navigate(`/o/${app.orgSlug}/admin`));
  app.action('connect:revoke', async (element) => {
    const clientId = (element.dataset.action ?? '').split(':')[2] ?? '';
    const session = app.state.session;
    if (clientId === '' || session === null) return;
    if (!globalThis.confirm(`Revoke ${clientId}? Its next request will be refused.`)) return;
    const revoked = await api.revokeClientGrant(app.client, clientId, key());
    if (!revoked.ok) {
      report(app, revoked.failure);
      return;
    }
    // The RPC's return value is the number of grant rows it tombstoned. Zero
    // is a successful call that changed nothing, and reporting it as a revocation
    // would be the specific lie this screen must not tell: a person reads
    // "revoked", stops using the client, and it is still live.
    if (revoked.value.revoked === 0) {
      app.notify(
        'bad',
        `No grant for ${clientId} was revoked. The call succeeded but matched no row, so this client is unchanged.`,
      );
      await app.reload();
      return;
    }
    app.notify(
      'ok',
      `${revoked.value.clientId} was revoked (${revoked.value.revoked} grant(s)). It fails the live-grant gate on its next request.`,
    );
    await app.reload();
  });

  /* ---- authorize ------------------------------------------------------ */

  app.action('authorize:reload', () => app.reload());
  app.action('authorize:approve', () => decide(app, 'approve'));
  app.action('authorize:deny', () => decide(app, 'deny'));
  app.action('authorize:revoke', async (element) => {
    const clientId = (element.dataset.action ?? '').split(':')[2] ?? '';
    const session = app.state.session;
    if (clientId === '' || session === null) return;
    if (!globalThis.confirm(`Revoke ${clientId}? Its next request will be refused.`)) return;
    const revoked = await api.revokeClientGrant(app.client, clientId, key());
    if (!revoked.ok) {
      report(app, revoked.failure);
      return;
    }
    // Zero rows revoked is a successful call that changed nothing, and it is
    // reported as such for the same reason `connect:revoke` above does: a
    // person who reads "revoked" stops using the client, and it is still live.
    if (revoked.value.revoked === 0) {
      app.notify(
        'bad',
        `No grant for ${clientId} was revoked. The call succeeded but matched no row, so this client is unchanged.`,
      );
      await app.reload();
      return;
    }
    app.notify(
      'ok',
      `${revoked.value.clientId} was revoked (${revoked.value.revoked} grant(s)). It fails the live-grant gate on its next request.`,
    );
    await app.reload();
  });

  /* ---- admin --------------------------------------------------------- */

  app.action('admin:reload', () => app.reload());
  app.action('admin:invite', async () => {
    const email = document.querySelector<HTMLInputElement>('#invite-email');
    const role = document.querySelector<HTMLSelectElement>('#invite-role');
    const session = app.state.session;
    if (email === null || role === null || session === null) return;
    const token = crypto.randomUUID().replace(/-/g, '') + crypto.randomUUID().replace(/-/g, '');
    const created = await api.createInvite(
      app.client,
      {
        email: email.value.trim(),
        role: role.value === 'admin' ? 'admin' : 'member',
        token,
      },
      key(),
    );
    if (!created.ok) {
      report(app, created.failure);
      return;
    }
    app.patch({ inviteLink: created.value.link });
    await app.reload();
  });
  app.action('admin:recover', async () => {
    const skillId = document.querySelector<HTMLInputElement>('#recovery-skill')?.value.trim() ?? '';
    const ownerId = document.querySelector<HTMLInputElement>('#recovery-owner')?.value.trim() ?? '';
    const reason =
      document.querySelector<HTMLTextAreaElement>('#recovery-reason')?.value.trim() ?? '';
    if (skillId === '' || ownerId === '' || reason === '') {
      app.notify(
        'bad',
        'Ownership transfer needs a skill id, a new owner id and a reason. All three are required.',
      );
      return;
    }
    const moved = await api.transferSkillOwnership(app.client, skillId, ownerId, reason, key());
    if (!moved.ok) {
      report(app, moved.failure);
      return;
    }
    app.notify(
      'ok',
      `Ownership of ${moved.value.skillId} transferred to ${moved.value.ownerId} and recorded in the audit log.`,
    );
  });
  app.action('admin:role', async (element) => {
    const parts = (element.dataset.action ?? '').split(':');
    const userId = parts[2];
    const role = parts[3] === 'admin' ? 'admin' : 'member';
    if (userId === undefined) return;
    const session = app.state.session;
    if (session === null) return;
    const changed = await api.setMembershipRole(app.client, session.orgId, userId, role, key());
    if (!changed.ok) {
      report(app, changed.failure);
      return;
    }
    app.notify('ok', `${person(changed.value.userId)} is now ${changed.value.role}.`);
    await app.reload();
  });
  app.action('admin:remove', async (element) => {
    const userId = (element.dataset.action ?? '').split(':')[2];
    const session = app.state.session;
    if (userId === undefined || session === null) return;
    if (!globalThis.confirm('Remove this member? Skills they own must be transferred first.'))
      return;
    const removed = await api.removeMembership(app.client, session.orgId, userId, key());
    if (!removed.ok) {
      report(app, removed.failure);
      return;
    }
    app.notify('ok', `${person(removed.value.userId)} was removed.`);
    await app.reload();
  });
  app.action('admin:self-review', async () => {
    const session = app.state.session;
    if (session === null) return;
    // The target state is the CURRENT setting, flipped — not a value carried in
    // the control's own markup. A control whose effect is decided by a string in
    // its own attributes is one markup edit away from setting the flag the
    // wrong way, and this is the flag that disables segregation of duties.
    const turnOn =
      document.querySelector<HTMLInputElement>('#self-review-allow')?.checked !== true;
    const reason =
      document.querySelector<HTMLTextAreaElement>('#self-review-reason')?.value.trim() ?? '';
    if (reason === '') {
      app.notify(
        'bad',
        'Changing this needs a reason. The change is audited, and the audit has to be able to say why.',
      );
      return;
    }
    const changed = await api.setSelfReviewPolicy(
      app.client,
      session.orgId,
      turnOn,
      reason,
      key(),
    );
    if (!changed.ok) {
      report(app, changed.failure);
      return;
    }
    app.notify(
      'ok',
      changed.value.allowSelfReview
        ? 'Self-review is now allowed. Every approval that uses it records itself on the review row and is audited.'
        : 'Self-review is no longer allowed. An author needs a different reviewer.',
    );
    await app.reload();
  });

  /* ---- gate ---------------------------------------------------------- */

  app.action('gate:sign-in', async () => {
    const token = document.querySelector<HTMLTextAreaElement>('#session-token')?.value.trim() ?? '';
    if (token === '') {
      app.notify('bad', 'Paste a session token to continue.');
      return;
    }
    const session = storeSession(token);
    if (session === null) {
      app.notify(
        'bad',
        'That is not a token this application can read. It must have a subject and an organization claim.',
      );
      return;
    }
    // The session goes into state BEFORE the organization read, because the
    // client sources its bearer token from `state.session` and not from
    // storage. It was set after, so the one request that decides whether the
    // token is any good went out unauthenticated and answered 401 — which made
    // signing in impossible on exactly the screen an expired session lands on,
    // so the unsaved text this feature keeps could never be recovered. See
    // `lib/unsaved.ts` for what that recovery is for.
    //
    // `adoptSession` and not a bare spread: the token names the organization
    // (`apps/api/src/identity.ts:378`), so this is an organization switch, and
    // the membership, client grants and review queue the PREVIOUS token loaded
    // would otherwise still be in the tab when the new organization's shell
    // first paints. AUTH-04: no data carried across.
    app.adoptSession(session);

    // The return path arrives from a query string, so it is attacker-controlled
    // and is put through the same guard the login screen uses when it renders
    // its links. It previously was not.
    //
    // The fallback cannot be a literal path, because a route is `/o/:slug/...`
    // and the login screen has no slug: the token carries the organization ID
    // and the ID is not the slug. So the organization is read to resolve it,
    // and a failure here is reported rather than navigated around, because
    // sending anyone to `/o//skills` would be a route that matches nothing.
    const params = new URLSearchParams(app.state.search);
    const organization = await api.getOrganization(app.client);
    if (!organization.ok) {
      // A token the server refused is not the current session. Leaving it in
      // place would make every later read answer 401 and every later attempt
      // look like the same expired-session page.
      clearStoredSession();
      app.adoptSession(null);
      report(app, organization.failure);
      return;
    }
    const next = safeReturnPath(params.get('return'), `/o/${organization.value.slug}/skills`);
    app.state = {
      ...app.state,
      organization: organization.value,
      screen: { kind: 'idle' },
    };
    app.notify('ok', 'Session accepted for this tab.');
    app.navigate(next);
  });

  app.action('gate:accept-invite', async () => {
    const route = app.state.route;
    if (route.name !== 'invite') return;
    const accepted = await api.acceptInvite(app.client, route.token, key());
    if (!accepted.ok) {
      report(app, accepted.failure);
      return;
    }
    app.notify('ok', 'Invitation accepted. Your membership now exists for this organization.');
    app.navigate(`/o/${app.orgSlug}/skills`);
  });
}

/**
 * Resolve one proposal.
 *
 * Accepting here records the decision and closes the queue item; it does not
 * create the revision draft that V1 section 6 describes, because that creation
 * is not available in this build. Requesting changes is expressed the same way,
 * with the difference stated to the person who pressed it: the queue has no
 * comment column, so a request for detail cannot be recorded without pretending
 * to resolve the item.
 */
async function resolveProposal(
  app: App,
  proposalId: string,
  decision: 'accepted' | 'dismissed',
  note: string,
): Promise<void> {
  if (proposalId === '') return;
  const session = app.state.session;
  if (session === null) return;
  if (decision === 'dismissed' && note === '') {
    app.notify('bad', 'A dismissal needs a reason. The audit trail has to be able to say why.');
    return;
  }
  const resolved = await api.resolveProposal(app.client, proposalId, {
    decision,
    note,
    resolvedBy: session.userId,
  });
  if (!resolved.ok) {
    report(app, resolved.failure);
    return;
  }
  app.notify(
    'ok',
    decision === 'accepted'
      ? 'Proposal accepted and recorded.'
      : 'Proposal dismissed with a reason.',
  );
  await app.reload();
}

function checked(element: HTMLElement): boolean {
  return element instanceof HTMLInputElement && element.checked;
}

function person(userId: string): string {
  return userId.slice(0, 8);
}

/**
 * Record the consent decision and put the outcome on screen.
 *
 * Two outcomes are NOT failures, and each is reported as what it is:
 *   `granted: true`  a grant row exists.
 *   `granted: false` the person pressed Deny. That is a completed decision,
 *                   so it is rendered as a declined state and not as an
 *                   error, and the fields the API returns beside it are not
 *                   shown as though a grant existed.
 * A non-2xx IS a refusal, and it replaces the screen with the failure panel.
 *
 * Neither branch navigates anywhere. This service mints no authorization code
 * (`apps/api/src/mcp/oauth/routes.ts` publishes no token endpoint), so there is
 * nothing to put in a redirect to `redirect_uri`; the page says so instead.
 */
async function decide(app: App, decision: ConsentDecision): Promise<void> {
  const data = currentScreenData<AuthorizeData>(app);
  if (data === null) return;
  const sent = await submitConsent(app, data, decision);
  if (!sent.ok) {
    app.setScreen({ kind: 'failed', failure: sent.failure });
    return;
  }
  app.setScreen({ kind: 'ready', data: { ...data, outcome: sent.value } });
}

function currentSkillId(app: App): string | null {
  return app.state.route.name === 'skill' || app.state.route.name === 'skill-edit'
    ? app.state.route.id
    : null;
}

function currentSlug(app: App): string | null {
  const data = currentScreenData<{ readonly skill: { slug: string } }>(app);
  return data === null ? null : data.skill.slug;
}

function currentScreenData<T>(app: App): T | null {
  return app.state.screen.kind === 'ready' ? (app.state.screen.data as T) : null;
}

/**
 * Ask for a publication, and keep watching it until it finishes.
 *
 * The screen is reloaded rather than patched, for two reasons. `loadReview` is
 * where the draft row comes from, and the draft's own `state` is what the
 * availability of the button is derived from — patching only the job would let
 * the two disagree, and a panel that offers Publish for a draft the database has
 * already moved to `publishing` is a control that lies. And a reload is the same
 * code path a person gets by pressing F5, so the panel cannot show a state the
 * screen would not reproduce on its own.
 */
async function requestPublication(app: App): Promise<void> {
  const data = currentScreenData<ReviewData>(app);
  if (data === null) return;

  const availability = publishAvailability(data.draft, data.publication);
  if (!availability.allowed) {
    app.notify('bad', `Not publishing: ${availability.reason}`);
    return;
  }

  // Set BEFORE the request and left on until the screen is reloaded, so a second
  // press into the same window is refused here rather than by the database. The
  // second press would carry a second idempotency key, and `app.publish_draft`
  // would refuse it as a duplicate release — correct, but as a 409 on a button
  // the person is looking at, which reads as the screen breaking.
  app.patch({ publishing: true });
  const requested = await api.publishDraft(app.client, data.draft.id, crypto.randomUUID());
  if (!requested.ok) {
    app.patch({ publishing: false });
    // Reloaded as well as reported. A refusal is usually the draft having moved
    // underneath the screen — somebody approved, edited or published it since
    // this panel was painted — and the freshest answer to "why" is the draft the
    // server sees, not the sentence this screen was holding.
    reportPublish(app, requested.failure);
    await app.reload();
    return;
  }

  // NOT "Published", and not "Publishing succeeded". The server accepted a
  // request; the worker has not been asked to do anything yet, and the crash
  // window between its `git push` and `finalizePublication` is real. The word
  // that would be a lie is the one this deliberately does not use.
  app.notify(
    'ok',
    'Publication requested and accepted by the queue. Nothing is published yet. The current release ' +
      'stays live until the new one is verified, and this panel reports what actually happened.',
  );
  // The reload is what arms the poll: `loadScreen` starts following a draft
  // whose publication is still running, so the follow-up reads one endpoint on
  // its own schedule instead of this function holding a timer.
  await app.reload();
}

/**
 * Refusals that deserve their own sentence.
 *
 * `presentFailure` titles every 409 "Someone else changed this first", which is
 * right for a version conflict and wrong for the two refusals
 * `app.publish_draft` raises: `approval_missing_conflict` and
 * `content_hash_conflict`. Both arrive as a `conflict` with the reason folded
 * into `version_conflict` by the API's own table, so the only thing that
 * distinguishes them is the database's own detail string — and that string is
 * the most useful thing on the screen. Sending somebody to reload a review
 * screen that is already correct, because the publication was refused for a
 * reason a reload does not touch, is the specific waste this avoids.
 */
function reportPublish(app: App, failure: Failure): void {
  if (failure.kind === 'conflict' && failure.detail !== '') {
    app.notify('bad', `The publication was refused. ${failure.detail}`);
    return;
  }
  report(app, failure);
}
